import { forwardRef, useEffect, useRef, useState } from "react";
import type { CSSProperties, FocusEvent, MouseEvent, ReactNode, RefObject } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, History, Lock, PanelLeftClose, PanelLeftOpen, RefreshCw, X } from "lucide-react";
import { TIER_NAMES } from "../components/TierLockModal";
import { Tooltip } from "../components/Tooltip";
import { countTag, theme } from "../theme";
import { safeHref } from "../utils/safeHref";
import type { LinkAdapter } from "./LinkAdapter";
import { isPlainLeftClick } from "./LinkAdapter";
import { ExperienceModePill, ExperienceModeRailButton } from "./ExperienceModeToggle";
import type { NavCounts } from "./NavCountsContext";
import type { NavBadgeKey, NavContextBlock, NavHeader, NavItem, NavModel, NavRoleTone, NavSection } from "./types";

// ── App sidebar ───────────────────────────────────────────────────────────
// Renders a NavModel and knows nothing about roles: the 230px column, the
// 56px icon rail with tooltips, the mobile drawer (a dialog that closes when a
// link is followed or a row opens a dialog / switches workspace, never on a
// group toggle), persisted group state, badges, locks and the footer. A task
// area (NavItem.tabs) is a collapsible group here, its pages the rows (finaliseNavModel
// sets `children`); a closed group shows its rows' summed badge. The same pages are tabs above the page
// (AreaTabs). The setup checklist lives on the What's next page, not here.

export const SIDEBAR_WIDTH = 284;
export const RAIL_WIDTH = 56;
export const DRAWER_WIDTH = 260;

export interface AppSidebarProps {
  model: NavModel;
  adapter: LinkAdapter;
  counts: NavCounts | null;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  isGroupOpen: (groupId: string, defaultOpen?: boolean) => boolean;
  onToggleGroup: (groupId: string, defaultOpen?: boolean) => void;
  isMobile: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  /** A locked (tier-gated) row was clicked. */
  onLocked: (item: NavItem) => void;
  version: string | null;
  /** The shell's version-history route (absolute app path). */
  versionTo: string;
  /** Guided / Expert in the footer (the client shell and the read-through). */
  showExperienceMode: boolean;
}

// ── Badge values ──────────────────────────────────────────────────────────

export type BadgeValue = number | "running" | null;

export function badgeValue(counts: NavCounts | null, key: NavBadgeKey | undefined): BadgeValue {
  if (!counts || !key) return null;
  if (counts.role === "client") {
    if (key === "actions") return counts.actions || null;
    if (key === "unresolvedFeedback") return counts.unresolvedFeedback || null;
    if (key === "resilience") return counts.resilience || null;
    if (key === "improvement") return counts.improvement || null;
    if (key === "aiSystems") return counts.aiSystems || null;
    if (key === "submissions") return counts.submissions || null;
    if (key === "governance") return counts.governance || null;
    if (key === "bia") return counts.bia || null;
    if (key === "vulnerabilities") return counts.vulnerabilities || null;
    if (key === "remediation") return counts.remediation || null;
    if (key === "people") return counts.people || null;
    if (key === "myTasks") return counts.myTasks || null;
    if (key === "dataDiscovery") return counts.dataDiscovery || null;
    if (key === "managementSystem") return counts.managementSystem || null;
    if (key === "runningCheck") return counts.runningCheck ? "running" : null;
    return null;
  }
  if (counts.role === "consultant") {
    if (key === "clientsNeedingAction") return counts.clientsNeedingAction || null;
    if (key === "openFindings") return counts.openFindings || null;
    if (key === "myActions") return counts.myActions || null;
    return null;
  }
  if (key === "myActions") return counts.myActions || null;
  if (key === "clientsPendingReview") return counts.clientsPendingReview || null;
  return null;
}

/** One figure for a folded group: the sum of its rows' counts ("running" wins, as it is the live one). */
function sumBadges(keys: NavBadgeKey[], counts: NavCounts | null): BadgeValue {
  let total = 0;
  for (const key of keys) {
    const v = badgeValue(counts, key);
    if (v === "running") return "running";
    if (typeof v === "number") total += v;
  }
  return total > 0 ? total : null;
}

function badgeText(value: BadgeValue): string {
  if (value === "running") return "check running";
  if (value === null) return "";
  return value > 99 ? "99+" : String(value);
}

// ── Style primitives (the language of the old Sidebar) ────────────────────

const rowBase: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 12px 6px 16px",
  // Square, so the "you are here" marker (.nav-pill) runs flush down the left edge.
  borderRadius: 0,
  marginBottom: 2,
  fontSize: 13,
  fontWeight: 500,
  textDecoration: "none",
  position: "relative",
  transition: "background 0.11s, color 0.11s",
  cursor: "pointer",
  border: "none",
  width: "100%",
  textAlign: "left",
  fontFamily: theme.font,
  minWidth: 0,
};

const subBase: CSSProperties = {
  ...rowBase,
  padding: "5px 12px 5px 32px",
  marginBottom: 1,
  fontSize: 12,
  fontWeight: 400,
};

const railBase: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 36,
  margin: "0 auto 2px",
  borderRadius: 0,
  position: "relative",
  border: "none",
  cursor: "pointer",
  textDecoration: "none",
  fontFamily: theme.font,
  fontSize: 13,
  fontWeight: 700,
  padding: 0,
};

function rowColors(active: boolean, hov: boolean): CSSProperties {
  return {
    color: active ? "var(--c-heading)" : hov ? "var(--c-text)" : "var(--c-muted)",
    background: active || hov ? "var(--c-panelAlt)" : "transparent",
    ...(active ? { fontWeight: 600 } : {}),
  };
}

export function rolePill(tone: NavRoleTone): CSSProperties {
  if (tone === "consultant") return { background: "var(--c-consultantBg)", color: "var(--c-consultant)" };
  if (tone === "auditor") return { background: "var(--c-auditorBg)", color: "var(--c-auditor)" };
  return { background: "var(--c-accentBg)", color: "var(--c-accentText)" };
}

function Dot({ color, size = 7 }: { color: string; size?: number }) {
  return <span aria-hidden="true" style={{ width: size, height: size, borderRadius: "50%", background: color, flexShrink: 0, display: "inline-block" }} />;
}

function ItemGlyph({ item, active, size = 15 }: { item: NavItem; active: boolean; size?: number }) {
  if (item.icon) {
    const Icon = item.icon;
    return <span style={{ opacity: active ? 1 : 0.7, flexShrink: 0, display: "flex" }}><Icon size={size} /></span>;
  }
  if (item.dot) return <Dot color={item.dot} size={Math.round(size * 0.55)} />;
  return null;
}

function Badge({ value, rail = false }: { value: BadgeValue; rail?: boolean }) {
  if (value === null) return null;
  if (value === "running") {
    return (
      <span
        role="img"
        aria-label="Compliance check running"
        title="A compliance check is running"
        style={rail
          ? { position: "absolute", top: 4, right: 4, display: "flex", color: "var(--c-accent)" }
          : { marginLeft: "auto", display: "flex", color: "var(--c-accent)", flexShrink: 0 }}
      >
        <RefreshCw size={11} style={{ animation: "spin 1.1s linear infinite" }} />
      </span>
    );
  }
  const text = badgeText(value);
  return (
    <span
      style={rail
        ? { position: "absolute", top: 3, right: 3, fontSize: 9, fontWeight: 800, lineHeight: "13px", minWidth: 13, padding: "0 3px", borderRadius: 2, background: "var(--c-accent)", color: "#fff", textAlign: "center" }
        : { marginLeft: "auto", ...countTag }}
    >
      {text}
    </span>
  );
}

function Chip({ text }: { text: string | null | undefined }) {
  if (!text) return null;
  return (
    <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, lineHeight: "17px", padding: "0 6px", borderRadius: 2, background: "var(--c-panelAlt)", border: "1px solid var(--c-border)", color: "var(--c-heading)", flexShrink: 0 }}>
      {text}
    </span>
  );
}

function SectionLabel({ children, rail }: { children: ReactNode; rail: boolean }) {
  if (rail) return <div role="separator" style={{ height: 1, background: "var(--c-border)", margin: "8px 12px" }} />;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 16px 5px" }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px", textTransform: "uppercase", color: "var(--c-faint)", whiteSpace: "nowrap" }}>
        {children}
      </span>
      <span style={{ flex: 1, height: 1, background: "var(--c-border)" }} />
    </div>
  );
}

interface IconButtonProps {
  label: string;
  onClick: () => void;
  children: ReactNode;
  style?: CSSProperties;
  // Forwarded so a wrapping Tooltip can observe hover and focus.
  onMouseEnter?: (e: MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (e: MouseEvent<HTMLButtonElement>) => void;
  onFocus?: (e: FocusEvent<HTMLButtonElement>) => void;
  onBlur?: (e: FocusEvent<HTMLButtonElement>) => void;
  "aria-describedby"?: string;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, onClick, children, style, onMouseEnter, onMouseLeave, onFocus, onBlur, ...rest },
  ref,
) {
  const [hov, setHov] = useState(false);
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      aria-describedby={rest["aria-describedby"]}
      onClick={onClick}
      onMouseEnter={(e) => { setHov(true); onMouseEnter?.(e); }}
      onMouseLeave={(e) => { setHov(false); onMouseLeave?.(e); }}
      onFocus={onFocus}
      onBlur={onBlur}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 28, height: 28, borderRadius: 3, border: "none", cursor: "pointer", padding: 0,
        color: hov ? "var(--c-text)" : "var(--c-faint)", background: hov ? "var(--c-panelAlt)" : "transparent",
        transition: "background 0.11s, color 0.11s", flexShrink: 0, ...style,
      }}
    >
      {children}
    </button>
  );
});

// ── Rows ──────────────────────────────────────────────────────────────────

interface RowContext {
  adapter: LinkAdapter;
  counts: NavCounts | null;
  rail: boolean;
  onNavigated: () => void;
  onLocked: (item: NavItem) => void;
}

function LinkRow({ item, depth, ctx }: { item: NavItem; depth: 0 | 1; ctx: RowContext }) {
  const [hov, setHov] = useState(false);
  const { adapter, rail } = ctx;
  const to = item.to ?? "/";
  const active = adapter.isActive(to, item.activeMatch, item.exact);
  const badge = badgeValue(ctx.counts, item.badge);
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(e)) return;
    e.preventDefault();
    adapter.navigate(to);
    ctx.onNavigated();
  };

  if (rail) {
    const tip = badge ? `${item.label} (${badgeText(badge)})` : item.label;
    return (
      <Tooltip label={tip}>
        <a
          href={adapter.href(to)}
          aria-label={item.label}
          aria-current={active ? "page" : undefined}
          onClick={onClick}
          onMouseEnter={() => setHov(true)}
          onMouseLeave={() => setHov(false)}
          style={{ ...railBase, ...rowColors(active, hov) }}
        >
          {active && <span className="nav-pill" />}
          <ItemGlyph item={item} active={active} size={16} />
          {!item.icon && !item.dot && <span aria-hidden="true">{item.label.charAt(0).toUpperCase()}</span>}
          <Badge value={badge} rail />
        </a>
      </Tooltip>
    );
  }

  const hasGlyph = !!(item.icon || item.dot);
  return (
    <a
      href={adapter.href(to)}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...(depth === 1 ? subBase : rowBase),
        ...(depth === 0 && !hasGlyph ? { fontSize: 12.5, fontWeight: 400 } : {}),
        ...rowColors(active, hov),
      }}
    >
      {active && <span className="nav-pill" />}
      <ItemGlyph item={item} active={active} />
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>
      <Badge value={badge} />
      {!badge && <Chip text={item.chip} />}
    </a>
  );
}

function ActionRow({ item, depth, ctx }: { item: NavItem; depth: 0 | 1; ctx: RowContext }) {
  const [hov, setHov] = useState(false);
  const badge = badgeValue(ctx.counts, item.badge);
  // The drawer closes first (a no-op off-mobile) so the dialog the action opens
  // restores focus to the hamburger, not to a row under the backdrop.
  const onClick = () => { ctx.onNavigated(); item.onSelect?.(); };

  if (ctx.rail) {
    return (
      <Tooltip label={item.label}>
        <button type="button" aria-label={item.label} onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{ ...railBase, ...rowColors(false, hov) }}>
          <ItemGlyph item={item} active={false} size={16} />
          {!item.icon && !item.dot && <span aria-hidden="true">{item.label.charAt(0).toUpperCase()}</span>}
          <Badge value={badge} rail />
        </button>
      </Tooltip>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ ...(depth === 1 ? subBase : rowBase), ...rowColors(false, hov) }}
    >
      <ItemGlyph item={item} active={false} />
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>
      <Badge value={badge} />
    </button>
  );
}

function LockedRow({ item, depth, ctx }: { item: NavItem; depth: 0 | 1; ctx: RowContext }) {
  const [hov, setHov] = useState(false);
  const minTier = item.lock?.minTier ?? 0;
  const tierName = TIER_NAMES[minTier] ?? `tier ${minTier}`;
  const label = `${item.label} (requires ${tierName})`;
  // Close the drawer before the modal opens, so its focus-restore target is
  // the hamburger rather than a row under the backdrop.
  const onClick = () => { ctx.onNavigated(); ctx.onLocked(item); };

  // Greyed, but readable: the label stays at the muted text tone (AA against
  // the panel) and lighter weight; the "locked" cue lives on the icons.
  if (ctx.rail) {
    return (
      <Tooltip label={label}>
        <button type="button" aria-label={label} onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{ ...railBase, color: "var(--c-muted)", background: hov ? "var(--c-panelAlt)" : "transparent" }}>
          <ItemGlyph item={item} active={false} size={16} />
          <span style={{ position: "absolute", top: 4, right: 5, display: "flex", color: "var(--c-subtle)" }}><Lock size={9} /></span>
        </button>
      </Tooltip>
    );
  }

  return (
    <button
      type="button"
      aria-label={label}
      title={`Requires ${tierName}`}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...(depth === 1 ? subBase : rowBase),
        color: "var(--c-muted)",
        fontWeight: 400,
        background: hov ? "var(--c-panelAlt)" : "transparent",
      }}
    >
      <span style={{ display: "flex", color: "var(--c-subtle)" }}><ItemGlyph item={item} active={false} /></span>
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>
      <Lock size={12} style={{ flexShrink: 0, marginLeft: "auto", color: "var(--c-subtle)" }} />
    </button>
  );
}

function GroupRow({ item, ctx, open, onToggle }: { item: NavItem; ctx: RowContext; open: boolean; onToggle: () => void }) {
  const [hov, setHov] = useState(false);
  const children = (item.children ?? []).filter((c) => !c.hidden);
  const { adapter, rail } = ctx;
  const activeChild = children.find((c) => c.to && adapter.isActive(c.to, c.activeMatch, c.exact));
  const isOpen = open || !!activeChild;
  const groupBadge = sumBadges([item.badge, ...children.map((c) => c.badge)].filter((b, i, all) => b && all.indexOf(b) === i) as NavBadgeKey[], ctx.counts);

  if (rail) {
    const target = activeChild ?? children.find((c) => c.to);
    if (!target?.to) return null;
    const tip = item.chip ? `${item.label} (${item.chip})` : item.label;
    const active = !!activeChild;
    const to = target.to;
    return (
      <Tooltip label={tip}>
        <a
          href={adapter.href(to)}
          aria-label={item.label}
          aria-current={active ? "page" : undefined}
          onClick={(e) => { if (!isPlainLeftClick(e)) return; e.preventDefault(); adapter.navigate(to); ctx.onNavigated(); }}
          onMouseEnter={() => setHov(true)}
          onMouseLeave={() => setHov(false)}
          style={{ ...railBase, ...rowColors(active, hov) }}
        >
          {active && <span className="nav-pill" />}
          {item.dot ? <Dot color={item.dot} size={10} /> : <ItemGlyph item={item} active={active} size={16} />}
        </a>
      </Tooltip>
    );
  }

  const groupId = `nav-group-${item.id}`;
  // The group holding the active page is forced open, so a click on its header
  // would change nothing visible while flipping the persisted state; it is
  // inert until the user navigates out of the group.
  const pinnedOpen = !!activeChild;
  return (
    <div style={{ marginBottom: 2 }}>
      <button
        type="button"
        onClick={() => { if (!pinnedOpen) onToggle(); }}
        aria-expanded={isOpen}
        aria-disabled={pinnedOpen || undefined}
        aria-controls={groupId}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: "flex", alignItems: "center", gap: 8, width: "100%",
          padding: "5px 12px", borderRadius: 3, border: "none",
          background: hov && !pinnedOpen ? "var(--c-panelAlt)" : "transparent",
          color: hov ? "var(--c-text)" : "var(--c-muted)",
          fontSize: 12.5, fontWeight: 600, cursor: pinnedOpen ? "default" : "pointer", textAlign: "left",
          transition: "background 0.11s, color 0.11s", fontFamily: theme.font, minWidth: 0,
        }}
      >
        {item.dot ? <Dot color={item.dot} /> : <ItemGlyph item={item} active={false} />}
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>
        {/* Closed, the group carries its rows' attention count so nothing needing action is hidden by folding it. */}
        {!isOpen && groupBadge !== null ? <Badge value={groupBadge} /> : <Chip text={item.chip} />}
        <span style={{ color: "var(--c-faint)", marginRight: 2, display: "flex", flexShrink: 0 }}>
          <ChevronRight size={11} style={{ transition: "transform 180ms", transform: isOpen ? "rotate(90deg)" : "none" }} />
        </span>
      </button>
      {isOpen && (
        <div id={groupId}>
          {children.map((child) => <NavRow key={child.id} item={child} depth={1} ctx={ctx} />)}
        </div>
      )}
    </div>
  );
}

function NavRow({ item, depth, ctx, isGroupOpen, onToggleGroup }: {
  item: NavItem; depth: 0 | 1; ctx: RowContext;
  isGroupOpen?: (id: string, defaultOpen?: boolean) => boolean; onToggleGroup?: (id: string, defaultOpen?: boolean) => void;
}) {
  if (item.hidden) return null;
  if (item.children) {
    const defaultOpen = item.defaultOpen ?? true;
    return <GroupRow item={item} ctx={ctx} open={isGroupOpen ? isGroupOpen(item.id, defaultOpen) : defaultOpen} onToggle={() => onToggleGroup?.(item.id, defaultOpen)} />;
  }
  if (item.lock) return <LockedRow item={item} depth={depth} ctx={ctx} />;
  if (item.onSelect && !item.to) return <ActionRow item={item} depth={depth} ctx={ctx} />;
  if (item.to) return <LinkRow item={item} depth={depth} ctx={ctx} />;
  return null;
}

// ── Header / context / footer ─────────────────────────────────────────────

interface HeaderProps {
  header: NavHeader;
  rail: boolean;
  isMobile: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Mobile drawer only: the close control, focused when the drawer opens. */
  onMobileClose: () => void;
  closeButtonRef: RefObject<HTMLButtonElement>;
}

function Header({ header, rail, isMobile, collapsed, onToggleCollapsed, onMobileClose, closeButtonRef }: HeaderProps) {
  const logo = safeHref(header.logoUrl) ?? "/abacas-logo.png";
  if (rail) {
    return (
      <>
        <div style={{ height: 54, display: "flex", alignItems: "center", justifyContent: "center", borderBottom: "1px solid var(--c-border)", flexShrink: 0 }}>
          <Tooltip label={header.title}>
            <img src={logo} alt={header.title} style={{ width: 30, height: 30, borderRadius: 3, objectFit: "cover", flexShrink: 0 }} />
          </Tooltip>
        </div>
        <div style={{ padding: "6px 0 0", display: "flex", justifyContent: "center", flexShrink: 0 }}>
          <Tooltip label="Expand navigation ( [ )">
            <IconButton label="Expand navigation" onClick={onToggleCollapsed} style={{ width: 40, height: 32 }}>
              <PanelLeftOpen size={15} />
            </IconButton>
          </Tooltip>
        </div>
      </>
    );
  }
  return (
    <div style={{ height: 54, display: "flex", alignItems: "center", padding: "0 8px 0 14px", borderBottom: "1px solid var(--c-border)", gap: 9, flexShrink: 0 }}>
      <img src={logo} alt="" style={{ width: 32, height: 32, borderRadius: 3, objectFit: "cover", flexShrink: 0 }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: "-0.4px", color: "var(--c-heading)", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {header.title}
        </div>
        {(header.subtitle || header.role) && (
          // The subtitle ("Consultant workspace", a client's tier name) has the
          // ~130px column to itself, without tracking; the role pill sits
          // beside it only while both fit, otherwise it wraps to its own line.
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "2px 5px", marginTop: 2, minWidth: 0 }}>
            {header.subtitle && (
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "var(--c-faint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                {header.subtitle}
              </span>
            )}
            {header.role && (
              <span style={{ fontSize: 8.5, padding: "0 4px", lineHeight: "13px", borderRadius: 3, fontWeight: 700, flexShrink: 0, ...rolePill(header.role.tone) }}>
                {header.role.label}
              </span>
            )}
          </div>
        )}
      </div>
      {isMobile ? (
        <IconButton ref={closeButtonRef} label="Close navigation" onClick={onMobileClose}>
          <X size={15} />
        </IconButton>
      ) : (
        <Tooltip label={collapsed ? "Expand navigation ( [ )" : "Collapse navigation ( [ )"} side="bottom">
          <IconButton label={collapsed ? "Expand navigation" : "Collapse navigation"} onClick={onToggleCollapsed}>
            {collapsed ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />}
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
}

function ContextBlock({ context, rail, onNavigated }: { context: NavContextBlock; rail: boolean; onNavigated: () => void }) {
  const [hov, setHov] = useState(false);
  if (rail) {
    return (
      <div style={{ padding: "6px 0 2px", borderBottom: "1px solid var(--c-border)", marginBottom: 4 }}>
        <Tooltip label={context.backLabel}>
          <Link to={context.backTo} aria-label={context.backLabel} onClick={onNavigated} style={{ ...railBase, height: 30, color: "var(--c-muted)" }}>
            <ArrowLeft size={14} />
          </Link>
        </Tooltip>
        <Tooltip label={context.subtitle ? `${context.title} - ${context.subtitle}` : context.title}>
          <div role="img" aria-label={context.title} style={{ ...railBase, height: 36, cursor: "default" }}>
            <span style={{ width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, background: "var(--c-accentBg)", color: "var(--c-accentText)" }}>
              {context.initial}
            </span>
          </div>
        </Tooltip>
      </div>
    );
  }
  return (
    <div style={{ padding: "10px 14px 10px", borderBottom: "1px solid var(--c-border)", marginBottom: 4 }}>
      <Link
        to={context.backTo}
        onClick={onNavigated}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 500, textDecoration: "none", color: hov ? "var(--c-text)" : "var(--c-muted)", transition: "color 0.11s" }}
      >
        <ArrowLeft size={12} /> {context.backLabel}
      </Link>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 8, minWidth: 0 }}>
        <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 800, background: "var(--c-accentBg)", color: "var(--c-accentText)", flexShrink: 0 }}>
          {context.initial}
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--c-heading)", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={context.title}>
            {context.title}
          </div>
          {context.subtitle && (
            <div style={{ fontSize: 11, color: "var(--c-subtle)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{context.subtitle}</div>
          )}
        </div>
        {context.score !== undefined && context.score !== null && (
          <span title="Latest compliance score" style={{ fontSize: 11, fontWeight: 700, lineHeight: "17px", padding: "0 7px", borderRadius: 2, background: "var(--c-panelAlt)", border: "1px solid var(--c-border)", color: "var(--c-heading)", flexShrink: 0 }}>
            {Math.round(context.score)}%
          </span>
        )}
      </div>
      {context.dots && context.dots.length > 0 && (
        <div role="group" aria-label="Standards" style={{ display: "flex", gap: 5, marginTop: 7, flexWrap: "wrap" }}>
          {context.dots.map((d, i) => (
            <span key={`${d.label}-${i}`} role="img" title={d.label} aria-label={d.label} style={{ width: 8, height: 8, borderRadius: "50%", background: d.color, display: "inline-block" }} />
          ))}
        </div>
      )}
    </div>
  );
}

function Footer({ version, versionTo, rail, showExperienceMode, onNavigated }: { version: string | null; versionTo: string; rail: boolean; showExperienceMode: boolean; onNavigated: () => void }) {
  const [hov, setHov] = useState(false);
  const label = version ? `Version history (v${version})` : "Version history";
  if (rail) {
    return (
      <div style={{ borderTop: "1px solid var(--c-border)", padding: "6px 0", flexShrink: 0 }}>
        {showExperienceMode && <ExperienceModeRailButton style={{ ...railBase, height: 32 }} />}
        <Tooltip label={label}>
          <Link to={versionTo} aria-label={label} onClick={onNavigated} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{ ...railBase, height: 32, marginBottom: 0, color: hov ? "var(--c-text)" : "var(--c-faint)", background: hov ? "var(--c-panelAlt)" : "transparent" }}>
            <History size={14} />
          </Link>
        </Tooltip>
      </div>
    );
  }
  return (
    <div style={{ borderTop: "1px solid var(--c-border)", flexShrink: 0 }}>
      {showExperienceMode && (
        <div style={{ padding: "10px 14px 2px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--c-faint)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6, paddingLeft: 2 }}>
            Experience mode
          </div>
          <ExperienceModePill />
        </div>
      )}
      <div style={{ padding: "9px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <Link
          to={versionTo}
          aria-label={label}
          onClick={onNavigated}
          onMouseEnter={() => setHov(true)}
          onMouseLeave={() => setHov(false)}
          style={{ fontSize: 10, color: hov ? "var(--c-text)" : "var(--c-faint)", textDecoration: hov ? "underline" : "none", transition: "color 0.11s" }}
        >
          {version ? `v${version}` : "Version history"}
        </Link>
        <span aria-hidden="true" style={{ fontSize: 9.5, color: "var(--c-faint)", whiteSpace: "nowrap" }}>
          <kbd style={{ padding: "0 4px", border: "1px solid var(--c-border)", borderRadius: 3, fontFamily: "inherit" }}>Ctrl</kbd>
          {" "}
          <kbd style={{ padding: "0 4px", border: "1px solid var(--c-border)", borderRadius: 3, fontFamily: "inherit" }}>K</kbd>
        </span>
      </div>
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────

export function AppSidebar({
  model, adapter, counts, collapsed, onToggleCollapsed, isGroupOpen, onToggleGroup,
  isMobile, mobileOpen, onMobileClose, onLocked, version, versionTo, showExperienceMode,
}: AppSidebarProps) {
  const rail = collapsed && !isMobile;
  const onNavigated = isMobile ? onMobileClose : () => {};
  const drawer = isMobile && mobileOpen;
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Escape closes the drawer (a keyboard action, not a click inside it).
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onMobileClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawer, onMobileClose]);

  // Opening the drawer moves focus onto its close control (after the slide-in
  // starts, so the transform transition does not swallow it); AppShell makes
  // the page behind it inert and onMobileClose returns focus to the hamburger.
  useEffect(() => {
    if (!drawer) return;
    const t = window.setTimeout(() => closeButtonRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [drawer]);

  const ctx: RowContext = { adapter, counts, rail, onNavigated, onLocked };

  const navStyle: CSSProperties = isMobile
    ? {
        position: "fixed",
        top: 0,
        left: 0,
        width: DRAWER_WIDTH,
        height: "100dvh",
        background: "var(--c-panel)",
        borderRight: "1px solid var(--c-border)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: 300,
        transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
        visibility: mobileOpen ? "visible" : "hidden",
        transition: "transform 240ms cubic-bezier(0.4,0,0.2,1), visibility 240ms",
        boxShadow: mobileOpen ? "4px 0 24px rgba(0,0,0,0.18)" : "none",
        fontFamily: theme.font,
      }
    : {
        width: rail ? RAIL_WIDTH : SIDEBAR_WIDTH,
        minWidth: rail ? RAIL_WIDTH : SIDEBAR_WIDTH,
        // Fill the layout rather than the viewport so a host that adds chrome
        // above the layout (the consultant's own-company strip) doesn't clip the footer.
        height: "100%",
        background: "var(--c-panel)",
        borderRight: "1px solid var(--c-border)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        flexShrink: 0,
        transition: "width 180ms ease, min-width 180ms ease",
        fontFamily: theme.font,
      };

  const sections = model.sections.filter((s) => s.items.some((i) => !i.hidden));

  return (
    <>
      {isMobile && (
        <div
          onClick={onMobileClose}
          aria-hidden="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 299,
            opacity: mobileOpen ? 1 : 0,
            pointerEvents: mobileOpen ? "auto" : "none",
            transition: "opacity 240ms",
          }}
        />
      )}
      <nav
        id="app-nav"
        aria-label="Main navigation"
        aria-hidden={isMobile && !mobileOpen ? true : undefined}
        role={drawer ? "dialog" : undefined}
        aria-modal={drawer ? true : undefined}
        style={navStyle}
      >
        <Header
          header={model.header}
          rail={rail}
          isMobile={isMobile}
          collapsed={collapsed}
          onToggleCollapsed={onToggleCollapsed}
          onMobileClose={onMobileClose}
          closeButtonRef={closeButtonRef}
        />

        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "8px 0 12px" }}>
          {model.context && <ContextBlock context={model.context} rail={rail} onNavigated={onNavigated} />}

          {sections.map((section: NavSection) => (
            <div key={section.id}>
              {section.label ? <SectionLabel rail={rail}>{section.label}</SectionLabel> : (rail ? null : <div style={{ height: 8 }} />)}
              <div style={{ padding: rail ? "0 8px" : "0 5px" }}>
                {section.items.map((item) => (
                  <NavRow key={item.id} item={item} depth={0} ctx={ctx} isGroupOpen={isGroupOpen} onToggleGroup={onToggleGroup} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <Footer version={version} versionTo={versionTo} rail={rail} showExperienceMode={showExperienceMode} onNavigated={onNavigated} />
      </nav>
    </>
  );
}
